import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios'; // Using a separate axios instance for public routes
import {
  Container, Box, Typography, Paper, IconButton, CircularProgress, Alert, alpha
} from '@mui/material';
import { format, startOfWeek, addDays, subWeeks, addWeeks, isToday, isPast, endOfDay, subDays, startOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { WTooltip } from '../components/ui/WTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getBrusselsDate } from '../utils/dateUtils';

const PublicCompanySchedule = () => {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(getBrusselsDate(), { weekStartsOn: 1 }));
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  const publicAxios = useMemo(() => {
    const token = sessionStorage.getItem(`public_token_${publicId}`);
    if (!token) return null;

    return axios.create({
      baseURL: process.env.NODE_ENV === 'production' ? 'https://api.madispo.com' : 'http://localhost:3000',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }, [publicId]);

  const fetchData = useCallback(async () => {
    if (!publicAxios) {
      // Si pas de token, on redirige. Le useEffect principal s'en chargera.
      return;
    }

    setLoading(true);
    try {
      const weekStart = format(currentWeek, 'yyyy-MM-dd');
      const weekEnd = format(addDays(currentWeek, 6), 'yyyy-MM-dd');

      const [schedulesRes, tasksRes, lastUpdateRes] = await Promise.all([
        publicAxios.get(`/public/schedule?startDate=${weekStart}&endDate=${weekEnd}`),
        publicAxios.get('/public/tasks'),
        publicAxios.get('/public/schedule/last-update')
      ]);
      setSchedules(schedulesRes.data);
      setTasks(tasksRes.data);
      setLastUpdate(lastUpdateRes.data.lastUpdate); // Mettre à jour lastUpdate après les autres fetchs
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        sessionStorage.removeItem(`public_token_${publicId}`);
        navigate(`/public/${publicId}`);
      } else {
        setError("Erreur lors du chargement du planning.");
      }
    } finally {
      setLoading(false);
    }
  }, [publicAxios, currentWeek, navigate, publicId]);

  useEffect(() => {
     if (!publicAxios) {
       // Redirige si le token n'est pas présent au chargement
       navigate(`/public/${publicId}`);
     } else {
       fetchData();
     }
   }, [publicAxios, currentWeek, navigate, publicId, fetchData]); // fetchData est stable grâce à useCallback

  // Effet pour le rafraîchissement automatique
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!publicAxios || document.hidden) return; // Ne pas rafraîchir si l'onglet est inactif
      try {
        const response = await publicAxios.get('/public/schedule/last-update');
        const newLastUpdate = response.data.lastUpdate;
        // Si la date de mise à jour du serveur est plus récente, on rafraîchit les données.
        if (newLastUpdate && new Date(newLastUpdate) > new Date(lastUpdate)) {
          fetchData();
        }
      } catch (error) {
        console.error("Erreur lors de la vérification des mises à jour:", error);
      }
    }, 1 * 60 * 1000); // Toutes les minutes

    return () => clearInterval(interval); // Nettoyage de l'intervalle
  }, [publicAxios, lastUpdate, fetchData]);

  const handlePrevWeek = () => {
    const newWeekStart = subWeeks(currentWeek, 1);
    // On ne met à jour que si la nouvelle semaine est autorisée
    if (!isPrevWeekDisabled) {
      setCurrentWeek(newWeekStart);
    }
  };
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));

  const isPrevWeekDisabled = useMemo(() => {
    const limitDate = startOfDay(subDays(getBrusselsDate(), 7));
    const endOfPrevWeek = endOfDay(addDays(subWeeks(currentWeek, 1), 6));
    return endOfPrevWeek < limitDate;
  }, [currentWeek]);

  const weekDays = Array.from({ length: 7 }).map((_, i) => addDays(currentWeek, i));

  // Group schedules by date and then by task
  const groupedSchedules = schedules.reduce((acc, schedule) => {
    const date = schedule.date;
    if (!acc[date]) acc[date] = {};
    if (!acc[date][schedule.Task.name]) acc[date][schedule.Task.name] = [];
    acc[date][schedule.Task.name].push(schedule);
    return acc;
  }, {});

  const uniqueUsers = useMemo(() => {
    const users = new Map();
    schedules.forEach(schedule => {
      if (!users.has(schedule.User.id) && schedule.User.phone) {
        users.set(schedule.User.id, schedule.User);
      }
    });
    return Array.from(users.values());
  }, [schedules]);

  return (
    <Container maxWidth="xl">
      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, mb: 2 }}>
        <IconButton onClick={handlePrevWeek} disabled={isPrevWeekDisabled}><ChevronLeftIcon /></IconButton>
        <Typography variant="h5" sx={{ mx: 3 }}>
          Semaine du {format(currentWeek, 'd MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton onClick={handleNextWeek}><ChevronRightIcon /></IconButton>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box component={Paper} sx={{ display: 'grid', gridTemplateColumns: 'minmax(150px, 1.5fr) repeat(7, 1fr)', border: '1px solid', borderColor: 'divider' }}>
            {/* Header Row */}
            <Box sx={{ borderRight: '1px solid', borderColor: 'divider', p: 1, backgroundColor: 'grey.100' }}>
              <Typography variant="h6" align="center">Tâches</Typography>
            </Box>
            {weekDays.map(day => (
              <Box key={day.toString()} sx={{ borderRight: '1px solid', borderColor: 'divider', boxShadow: isToday(day) ? 'inset 0 0 0 3px #4caf50' : 'none', p: 1, textAlign: 'center', backgroundColor: 'grey.100', opacity: isPast(endOfDay(day)) ? 0.6 : 1 }}>
                <Typography variant="subtitle1">{format(day, 'eeee', { locale: fr })}</Typography>
                <Typography variant="body2" color="text.secondary">{format(day, 'd/MM')}</Typography>
              </Box>
            ))}

            {/* Task Rows */}
            {tasks.map(task => (
              <React.Fragment key={task.id}>
                <Box sx={{ gridColumn: '1 / -1', borderTop: '1px solid', borderColor: 'divider' }} />
                <Box sx={{ borderRight: '1px solid', borderColor: 'divider', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography fontWeight="bold">{task.name}</Typography>
                </Box>
                {weekDays.map(day => {
                  const dateString = format(day, 'yyyy-MM-dd');
                  const schedulesForCell = groupedSchedules[dateString]?.[task.name] || [];
                  return (
                    <Box key={`${task.id}-${day.toString()}`} sx={{ borderRight: '1px solid', borderColor: 'divider', background: isToday(day) ? '#4caf4f1e' : 'none', p: 1, minHeight: '80px', opacity: isPast(endOfDay(day)) ? 0.6 : 1 }}>
                      {schedulesForCell.map(schedule => {
                        const displayName = (schedule.User.firstName && schedule.User.lastName) ? `${schedule.User.firstName} ${schedule.User.lastName}` : schedule.User.email;
                        const hasNote = schedule.note && schedule.note.trim() !== '';
                        const tooltipTitle = (
                          <React.Fragment>
                            <Typography color="inherit" sx={{ mt: 1 }}>{schedule.note}</Typography>
                          </React.Fragment>
                        );

                        if (hasNote) {
                          return (
                            <WTooltip key={schedule.id} title={tooltipTitle}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 0.5,
                                  mb: 0.5,
                                  backgroundColor: alpha('#4caf50', 0.2),
                                  textAlign: 'center',
                                  position: 'relative', // Pour positionner l'icône
                                }}
                              >
                                <Typography variant="caption" fontWeight="bold" display="block">{displayName}</Typography>
                                <Typography variant="caption" display="block">{`${schedule.startTime.substring(0, 5)} - ${schedule.endTime.substring(0, 5)}`}</Typography>
                                <InfoOutlinedIcon
                                  sx={{
                                    position: 'absolute',
                                    bottom: 2,
                                    right: 2,
                                    fontSize: '0.8rem',
                                    opacity: 0.7,
                                    color: 'text.secondary'
                                  }}
                                />
                              </Paper>
                            </WTooltip>
                          );
                        }
                        return (
                          <Paper
                            key={schedule.id}
                            elevation={2}
                            sx={{
                              p: 0.5,
                              mb: 0.5,
                              backgroundColor: alpha('#4caf50', 0.2),
                              textAlign: 'center'
                            }}
                          >
                            <Typography variant="caption" fontWeight="bold" display="block">{displayName}</Typography>
                            <Typography variant="caption" display="block">{`${schedule.startTime.substring(0, 5)} - ${schedule.endTime.substring(0, 5)}`}</Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </Box>
          {uniqueUsers.length > 0 && (
            <Paper sx={{ mt: 2, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Contacts</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {uniqueUsers.map(user => (
                  <Paper key={user.id} elevation={2} sx={{ p: 1, backgroundColor: 'grey.100', textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight="bold">{`${user.firstName} ${user.lastName}`}</Typography>
                    <Typography variant="body2">{user.phone}</Typography>
                  </Paper>
                ))}
              </Box>
            </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default PublicCompanySchedule;
